/**
 * OTelInput.js 2016-2019
 *
 * Emile Silas Sare (emile.silas@gmail.com)
 */

// thanks to https://github.com/jackocnr/intl-tel-input/
import "../dist/utils.js";
import countriesMap, {tCountry} from "./countriesMap";
import countryChooser from "./countryChooser";

type tChangeHandler = (c: tCountry) => void;
type tOptions = {
	cc2: string,
	//nationalMode: true,
	number: string,
	numberType: "MOBILE",
	preferredCountries: string[],
	showSamplePlaceholder: boolean,
	allowedCountries: () => string[]
};

const utils                    = (window as any).intlTelInputUtils,
	  defaultOptions: tOptions = {
		  cc2                  : "bj",
		  //nationalMode: false,
		  number               : "",
		  numberType           : "MOBILE",
		  preferredCountries   : ["bj"],
		  showSamplePlaceholder: true,
		  allowedCountries     : () => []
	  },
	  cleanPhoneString         = function (str: string) {
		  return "+" + str.replace(/[^\d -]/g, "")
						  .replace(/\s+/g, " ")
						  .replace(/-[^\d]/g, "-")
						  .replace(/^[^1-9]+/g, "");
	  };

class OTelInput {
	private phoneNumber: string              = "";
	private options: tOptions                = {} as tOptions;
	private currentCountry: tCountry         = {} as tCountry;
	private changeHandlers: tChangeHandler[] = [];

	constructor(options: any) {
		this._updateOptions(options);
	}

	onChange(handler: tChangeHandler) {
		if (typeof handler === "function") {
			this.changeHandlers.push(handler);
		}
	}

	setPhoneNumber(number: string) {
		number = cleanPhoneString(number);

		let dialCode = OTelInput.getDialCode(number),
			formatted;

		if (dialCode) {
			this.currentCountry = OTelInput.getCountryWithDialCode(dialCode) || this.currentCountry;
			formatted           = this._getFormat(number);

			this.phoneNumber = formatted;
		} else {
			this.phoneNumber = number;
		}

		return this._inform();
	}

	bindInput(input: HTMLInputElement, chooserSelector: any = null) {
		let m = this;

		m.unbindInput(input);

		$(input).on("keyup.o-tel-input", function (e) {
			m._inputEventHandler(e)
		}).on("paste.o-tel-input cut.o-tel-input", function (e) {
			// hack because "paste" event is fired before input is updated
			setTimeout(function () {
				m._inputEventHandler(e);
			});
		});

		chooserSelector && countryChooser(chooserSelector, m);

		m.onChange(function () {
			input.value = m.phoneNumber;
		});

		return m.setPhoneNumber(m.phoneNumber);
	}

	unbindInput(input: HTMLInputElement) {
		$(input).off(".o-tel-input");
		return this;
	}

	private _inputEventHandler(e: any) {
		let code   = e.which,
			input  = e.target,
			number = cleanPhoneString(input.value);

		// 8: backspace
		// 37: left arrow
		// 38: up arrow
		// 39: right arrow
		// 40: down arrow

		if (code === 8 || code === 37 || code === 38 || code === 39 || code === 40) {
			return;
		}

		if (number && number != "+") {

			this.setPhoneNumber(number);

			// console.log(e.type, code, number, this.phoneNumber);

			if (this.options.showSamplePlaceholder) {
				input.setAttribute("placeholder", this.getSample());
			}
		} else {
			input.value = this.phoneNumber = number;
		}
	}

	getCountriesAsHtml() {
		let only  = this.options.allowedCountries() || [],
			$list = $(countriesMap.html);

		if (only && only.length) {

			for (let i = 0; i < only.length; i++) {
				let cc2 = (only[i] || "__").toLowerCase();

				$list.find("li[data-o-tel-cc2='" + cc2 + "']")
					 .addClass("o-tel-cc2-ok");
			}

			$list.find("li:not(.o-tel-cc2-ok)").remove();
		}

		return $list;
	}

	setCountry(cc2: string) {
		let opt    = Object.assign({}, this.options);
		opt.cc2    = cc2;
		opt.number = "+" + countriesMap.countries[cc2.toLowerCase()].dialCode;

		this._updateOptions(opt);
	}

	private _updateOptions(options: tOptions): this {

		this.options        = Object.assign({}, defaultOptions, options || this.options || {});
		this.currentCountry = OTelInput.getCountryWithCc2(this.options.cc2);

		if (!this.options.number) {// if no number initialize to default cc2 dialCode
			this.options.number = "+" + countriesMap.countries[this.options.cc2.toLowerCase()].dialCode;
		}

		this.setPhoneNumber(this.options.number);

		return this;
	}

	getCurrentCountry(): tCountry {
		return this.currentCountry;
	}

	getOptions(): tOptions {
		return this.options;
	}

	isValid(number: string = this.phoneNumber): boolean {
		return utils.isValidNumber(number, this.currentCountry.cc2);
	}

	isPossible(number: string = this.phoneNumber): boolean {
		return utils.getValidationError(number, this.currentCountry.cc2) === utils.validationError.IS_POSSIBLE;
	}

	isFor(type: string, number: string = this.phoneNumber): boolean {
		return utils.getNumberType(number, this.currentCountry.cc2) === utils.numberType[type];
	}

	getSample(isNationalMode: boolean = false): string {
		let numberType = utils.numberType[this.options.numberType];

		return utils.getExampleNumber(this.currentCountry.cc2, Boolean(isNationalMode), numberType);
	}

	getInput(format: boolean = false) {
		return format ? this._getFormat(this.phoneNumber) : utils.formatNumber(this.phoneNumber, this.currentCountry.cc2);
	}

	static isPhoneNumberPossible(number: string, possible: boolean = false) {
		let instance = new OTelInput({"number": number});

		if (possible === true) {
			return instance.isPossible();
		}

		return instance.isValid() || (instance.isPossible() && instance.isFor("MOBILE"));
	}

	static getCountryWithCc2(cc2: string) {
		return countriesMap.countries[cc2.toLowerCase()];
	}

	static getCountryWithDialCode(dialCode: string): tCountry | null {
		let found = null;

		if (dialCode) {
			let cc2List = countriesMap.dialCodeToCc2[dialCode];

			for (let j = 0; j < cc2List.length; j++) {
				let first = cc2List[j];//may be null so we let it and go to the next if exists
				if (first) {
					found = OTelInput.getCountryWithCc2(first);
					break;
				}
			}
		}

		return found;
	}

	static getDialCode(str: string): string {
		let dialCode    = "",
			phoneNumber = String(str),
			numberReg   = /[0-9]/;

		// only interested in international numbers (starting with a plus)
		if (phoneNumber.charAt(0) === "+") {
			let numericChars = "";
			// iterate over chars
			for (let i = 0; i < phoneNumber.length; i++) {
				let c = phoneNumber.charAt(i);
				// if char is number
				if (numberReg.test(c)) {
					numericChars += c;
					// if current numericChars make a valid dial code
					if (countriesMap.dialCodeToCc2[numericChars]) {
						// store the actual raw string (useful for matching later)
						dialCode = numericChars;
					}
					// longest dial code is 4 chars
					if (numericChars.length === 4) {
						break;
					}
				}
			}
		}

		return dialCode;
	}

	private _inform(): this {
		for (let i = 0; i < this.changeHandlers.length; i++) {
			let cb = this.changeHandlers[i];
			cb(this.currentCountry);
		}

		return this;
	}

	private _getFormat(number: string, isNationalMode: boolean = false): string {
		let run = number && number.trim().length > 1;
		if (run) {
			let format = (isNationalMode || number.charAt(0) !== "+") ? utils.numberFormat.NATIONAL : utils.numberFormat.INTERNATIONAL;
			number     = utils.formatNumber(number, this.currentCountry.cc2, format);
		}

		return number;
	}
}

export default OTelInput;